import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import { executeQuery as executeQueryApi } from '../api';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';

/**
 * Convert data to CSV format
 */
function convertToCSV(data) {
  if (!data || data.length === 0) {
    return '';
  }

  // Get headers from first row
  const headers = Object.keys(data[0]);
  
  // Escape CSV field
  const escapeCsvField = (field) => {
    if (field === null || field === undefined) {
      return '';
    }
    const str = String(field);
    // If the field contains a comma, double quote, or newline, enclose it in double quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      // Within a quoted field, any double quotes must be escaped by another double quote
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Build CSV
  const csvRows = [];
  
  // Add header row
  csvRows.push(headers.map(escapeCsvField).join(','));
  
  // Add data rows
  for (const row of data) {
    const values = headers.map(header => escapeCsvField(row[header]));
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(data, filename = 'query-results.csv') {
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default function RunQuery() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRunQuery = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await executeQueryApi(query);
      
      if (response.success) {
        setResults(response.data);
        setError(null);
      } else {
        setError(response.error || 'An error occurred while executing the query');
        setResults(null);
      }
    } catch (err) {
      console.error('Error executing query:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while executing the query');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (results && results.length > 0) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCSV(results, `query-results-${timestamp}.csv`);
    }
  };

  const handleKeyDown = (e) => {
    // Allow Ctrl+Enter to run query
    if (e.ctrlKey && e.key === 'Enter') {
      handleRunQuery();
    }
  };

  return (
    <Box>
      <PageHeader title="Run Query" />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Execute SELECT queries against the database. Only SELECT statements are allowed.
          Press Ctrl+Enter to run the query.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={8}
          variant="outlined"
          label="SQL Query"
          placeholder="SELECT * FROM users LIMIT 10;"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2, fontFamily: 'monospace' }}
          InputProps={{
            sx: { fontFamily: 'monospace', fontSize: '0.9rem' }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleRunQuery}
            disabled={loading || !query.trim()}
          >
            {loading ? 'Running...' : 'Run'}
          </Button>

          {results && results.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && <LoadingBlock height={200} />}

      {!loading && results && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Results ({results.length} row{results.length !== 1 ? 's' : ''})
            </Typography>
          </Box>

          {results.length === 0 ? (
            <Alert severity="info">No results found</Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {Object.keys(results[0]).map((column) => (
                      <TableCell key={column} sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {Object.keys(results[0]).map((column) => (
                        <TableCell key={column}>
                          {row[column] === null ? (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              NULL
                            </Typography>
                          ) : (
                            String(row[column])
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}

